import {
  ComponentProps,
  createContext,
  PropsWithChildren,
  ReactNode,
  useContext,
  useState,
} from "react";
import Animated, {
  Easing,
  ReduceMotion,
  useAnimatedStyle,
  withTiming,
} from "react-native-reanimated";
import { createPortal } from "react-dom";
import {
  Stack,
  StackProps,
  useWindowDimensions,
  View,
  ViewProps,
} from "tamagui";

interface SideBar {
  toggle: () => void;
  open: boolean;
}

const SideBarContext = createContext<SideBar>({
  open: false,
  toggle: () => console.warn("should be called inside SideBarProvider"),
});

const useSideBar = () => {
  const context = useContext(SideBarContext);
  if (!context) {
    throw new Error("should be used inside SideBarContext");
  }
  return context;
};

const SideBarProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [open, setOpen] = useState(false);

  return (
    <SideBarContext.Provider
      value={{
        open,
        toggle: () => setOpen((prev) => !prev),
      }}
    >
      {children}
    </SideBarContext.Provider>
  );
};

const SideBarOverlay: React.FC<ViewProps> = (props) => {
  const { open, toggle } = useSideBar();

  const { width, height } = useWindowDimensions();

  if (!open) return null;
  return (
    <View
      zIndex={5555}
      onPress={toggle}
      position={"absolute"}
      width={width}
      height={height}
      {...props}
    />
  );
};

type TriggerRenderFunction = (val: boolean) => React.ReactNode;

type TriggerChildren = TriggerRenderFunction | ReactNode;

function isRenderFunction(
  child: TriggerChildren,
): child is TriggerRenderFunction {
  return typeof child === "function";
}

const Trigger: React.FC<
  Omit<StackProps, "children"> & { children?: TriggerChildren }
> = ({ children, ...rest }) => {
  const { open, toggle } = useSideBar();
  return (
    <Stack onPress={toggle} zIndex={7777} {...rest}>
      {isRenderFunction(children) ? children(open) : children}
    </Stack>
  );
};

const Close: React.FC<StackProps> = ({ children, ...rest }) => {
  const { toggle } = useSideBar();

  return (
    <Stack onPress={toggle} {...rest}>
      {children}
    </Stack>
  );
};

type Portion = "1/3" | "1/2" | "3/4";

type ContentProps = ComponentProps<typeof Animated.View> & {
  portion?: Portion;
  startPosition?: "left" | "right";
};

const getSideBarWidth = (width: number, portion: Portion) => {
  switch (portion) {
    case "1/2":
      return Math.round(width / 2);
    case "1/3":
      return Math.round(width / 3);
    case "3/4":
      return Math.round((width / 100) * 75);
    default:
      return width;
  }
};

const Portal: React.FC<PropsWithChildren> = ({ children }) => {
  const host = globalThis.document.getElementById("root");

  const { open } = useSideBar();
  if (!host) return null;
  if (!open) return null;

  return createPortal(
    <View style={{ position: "absolute", width: "100vw", height: "100dvh" }}>
      {children}
    </View>,
    host,
  );
};

const Content: React.FC<ContentProps> = ({
  children,
  portion = "1/2",
  startPosition = "right",
  ...rest
}) => {
  const { width, height } = useWindowDimensions();

  const { open } = useSideBar();
  const sidebarWidth = getSideBarWidth(width, portion);

  const animated = useAnimatedStyle(() => {
    return {
      position: "absolute",
      top: 0,
      bottom: 0,
      width: sidebarWidth,
      ...(startPosition === "right"
        ? {
            right: -sidebarWidth,
          }
        : {
            left: -sidebarWidth,
          }),

      zIndex: 9999,
      transform: [
        {
          translateX: withTiming(
            open
              ? startPosition === "right"
                ? -sidebarWidth
                : sidebarWidth
              : startPosition === "right"
                ? sidebarWidth
                : -sidebarWidth,
            {
              duration: 300,
              easing: Easing.inOut(Easing.ease),
              reduceMotion: ReduceMotion.Never,
            },
          ),
        },
      ],
    };
  });

  return (
    <Animated.View
      style={[
        animated,
        {
          height,
        },
      ]}
      {...rest}
    >
      {children}
    </Animated.View>
  );
};

export const SideBar = {
  Provider: SideBarProvider,
  Overlay: SideBarOverlay,
  Trigger,
  Close,
  Content,
  Portal,
};
